
import Vue from "vue";

/**
 * Leichtgewichtiger Audio-Recorder auf Basis der nativen MediaRecorder-API.
 * Das Encoding macht der Browser (off main thread), daher kein UI-Ruckeln
 * wie beim alten vue-audio-recorder (lamejs im Main-Thread).
 * Format: webm/opus (Chrome/Firefox) bzw. mp4/aac (Safari) — beides
 * wird von Whisper akzeptiert.
 */
export default Vue.extend({
  name: "AudioRecorder",
  props: {
    // 0 = kein Limit (Standard)
    maxSeconds: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      recording: false,
      paused: false,
      recordedBlob: null as Blob | null,
      previewUrl: null as string | null,
      seconds: 0,
      errorMessage: null as string | null,
      recorder: null as MediaRecorder | null,
      stream: null as MediaStream | null,
      timer: null as number | null,
    };
  },
  computed: {
    formattedSeconds(): string {
      const minutes = Math.floor(this.seconds / 60);
      const remainder = this.seconds % 60;
      return (
        String(minutes).padStart(2, "0") + ":" + String(remainder).padStart(2, "0")
      );
    },
    pauseSupported(): boolean {
      return !!this.recorder && typeof this.recorder.pause === "function";
    },
  },
  beforeDestroy() {
    this.cleanup();
    this.revokePreview();
  },
  methods: {
    supportedMimeType(): string {
      if (typeof MediaRecorder === "undefined") {
        return "";
      }
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
      return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) || "";
    },
    async startRecording() {
      this.errorMessage = null;
      this.recordedBlob = null;
      this.revokePreview();
      try {
        // eslint-disable-next-line
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.stream = stream;
        const mimeType = this.supportedMimeType();
        this.recorder = new MediaRecorder(
            stream,
            mimeType ? { mimeType } : undefined
        );
        const chunks: Blob[] = [];
        this.recorder.ondataavailable = (event: BlobEvent) => {
          if (event.data && event.data.size > 0) {
            chunks.push(event.data);
          }
        };
        this.recorder.onstop = () => {
          this.clearTimer();
          this.stopTracks();
          if (chunks.length > 0) {
            const blob = new Blob(chunks, {
              type: (this.recorder && this.recorder.mimeType) || "audio/webm",
            });
            this.recordedBlob = blob;
            this.previewUrl = window.URL.createObjectURL(blob);
            this.$emit("recorded", blob);
          }
          this.recording = false;
          this.paused = false;
        };
        this.seconds = 0;
        this.paused = false;
        this.startTimer();
        this.recording = true;
        this.recorder.start();
      } catch (error) {
        this.errorMessage = this.$t(
            "Mikrofon nicht verfügbar oder Zugriff verweigert"
        ) as string;
      }
    },
    tick() {
      this.seconds += 1;
      if (this.maxSeconds > 0 && this.seconds >= this.maxSeconds) {
        this.stopRecording();
      }
    },
    stopRecording() {
      if (this.recorder && this.recording) {
        this.recorder.stop();
      }
    },
    togglePause() {
      if (!this.recorder) {
        return;
      }
      if (this.paused) {
        this.recorder.resume();
        this.paused = false;
        this.startTimer();
      } else {
        this.recorder.pause();
        this.paused = true;
        this.clearTimer();
      }
    },
    startTimer() {
      this.clearTimer();
      this.timer = window.setInterval(this.tick, 1000);
    },
    discard() {
      this.recordedBlob = null;
      this.seconds = 0;
      this.paused = false;
      this.revokePreview();
      this.$emit("discarded");
    },
    reset() {
      this.cleanup();
      this.discard();
    },
    revokePreview() {
      if (this.previewUrl) {
        window.URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
    },
    stopTracks() {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
    },
    clearTimer() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    },
    cleanup() {
      this.clearTimer();
      if (this.recorder && this.recorder.state !== "inactive") {
        this.recorder.onstop = null;
        this.recorder.stop();
      }
      this.stopTracks();
      this.recording = false;
    },
  },
});
